import React, { useState, useEffect, useCallback, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  DashboardCard,
  StatsGrid,
  BaseFilter,
  type BaseFilterState,
  type FilterConfig,
  type StatItem,
} from '../../../shared/ui';
import {
  ledgerV2Api,
  type ExecutorBalanceV2Item,
  type LedgerV2Item,
} from '../../../api/ledgerV2Api';
import styles from './ExecutorBalancesCard.module.css';
import { parseApiDateTimeUtc } from '../../../shared/utils/timeFormatter';
import { useAuth } from '../../../shared/contexts/AuthContext';

interface ExecutorBalancesCardProps {
  siteId: number;
  /** Не показывать карточку в списке — только fullscreen по вызову openWithExecutor / openPersonal */
  listHidden?: boolean;
  /** Только личные проводки текущего пользователя (без права FINANCE_VIEW_DATA) */
  personalOnly?: boolean;
  /** Заголовок fullscreen-карточки */
  title?: string;
}

export interface ExecutorBalancesCardHandle {
  openWithExecutor: (siteExecutorId: number) => void;
  openPersonal: () => void;
}

function movementTypeLabel(code: string): string {
  switch (code) {
    case 'earning':
      return 'Начисление';
    case 'correction':
      return 'Корректировка';
    case 'payment':
    case 'payment_document':
      return 'Платёж';
    default:
      return code || 'Проводка';
  }
}

/** Начисления и коррекции в одной сумме для сводки за день. */
function sumAccrualsAndCorrections(rows: LedgerV2Item[]): number {
  return rows.reduce((s, row) => {
    const mt = row.movementType;
    if (mt === 'earning' || mt === 'correction') return s + (row.amount ?? 0);
    return s;
  }, 0);
}

/** Сумма платежей (обычно отрицательная). */
function sumPayments(rows: LedgerV2Item[]): number {
  return rows.reduce((s, row) => (row.movementType === 'payment' ? s + (row.amount ?? 0) : s), 0);
}

/** Округление до копеек после суммирования на клиенте. */
function roundMoneyClient(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Сортировка по времени записи журнала (для конца периода — последняя строка). */
function sortLedgerChronologically(rows: LedgerV2Item[]): LedgerV2Item[] {
  return [...rows].sort((a, b) => {
    const ta = parseApiDateTimeUtc(a.createdAt).getTime();
    const tb = parseApiDateTimeUtc(b.createdAt).getTime();
    if (ta !== tb) return ta - tb;
    return a.id - b.id;
  });
}

/** Как начисление — по earningTypeName. */
function movementDetailLine(row: LedgerV2Item): string {
  const base = movementTypeLabel(row.movementType);
  if ((row.movementType === 'earning' || row.movementType === 'correction') && row.earningTypeName) {
    return `${base} · ${row.earningTypeName}`;
  }
  return base;
}

const ExecutorBalancesCard = forwardRef<ExecutorBalancesCardHandle, ExecutorBalancesCardProps>(
  ({ siteId, listHidden = false, personalOnly = false, title = 'Баланс' }, ref) => {
  const { user } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [items, setItems] = useState<LedgerV2Item[]>([]);
  const [executorBalances, setExecutorBalances] = useState<ExecutorBalanceV2Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  /** Доступ к данным журнала (личный — без FINANCE_VIEW_DATA; общий — с правом) */
  const [hasLedgerAccess, setHasLedgerAccess] = useState(true);
  const [filters, setFilters] = useState<BaseFilterState>({});
  /** Развёрнутые строки журнала — детали одной проводки */
  const [expandedLedgerIds, setExpandedLedgerIds] = useState<Set<number>>(new Set());
  /** Ключ вида `${dateKey}::${siteExecutorId}` — развёрнута ли детализация за день по исполнителю */
  const [expandedDayExecutorKeys, setExpandedDayExecutorKeys] = useState<Set<string>>(new Set());

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const isOpen = !isCollapsed || isFullscreenOpen;
    if (wasOpenRef.current && !isOpen) {
      setItems([]);
      setExecutorBalances([]);
      setLoadError(null);
      setExpandedDayExecutorKeys(new Set());
      setExpandedLedgerIds(new Set());
      setLoading(false);
    }
    wasOpenRef.current = isOpen;
  }, [isCollapsed, isFullscreenOpen]);

  const filterConfig: FilterConfig[] = useMemo(
    () =>
      personalOnly
        ? [{ type: 'period', label: 'Период', enabled: true }]
        : [
            { type: 'period', label: 'Период', enabled: true },
            { type: 'user', label: 'Исполнитель', enabled: true },
          ],
    [personalOnly]
  );

  const baseFilters: BaseFilterState = useMemo(
    () => ({
      startDate: filters.startDate,
      endDate: filters.endDate,
      siteExecutorId: filters.siteExecutorId,
      userId: filters.userId,
    }),
    [filters.startDate, filters.endDate, filters.siteExecutorId, filters.userId]
  );

  const handleBaseFiltersChange = useCallback((next: BaseFilterState) => {
    setFilters({
      startDate: next.startDate,
      endDate: next.endDate,
      siteExecutorId: next.siteExecutorId,
      userId: next.userId,
    });
  }, []);

  useImperativeHandle(ref, () => ({
    openWithExecutor(siteExecutorId: number) {
      setFilters({
        startDate: undefined,
        endDate: undefined,
        siteExecutorId,
        userId: undefined,
      });
      setExpandedDayExecutorKeys(new Set());
      setExpandedLedgerIds(new Set());
      setIsFullscreenOpen(true);
    },
    openPersonal() {
      setFilters({
        startDate: undefined,
        endDate: undefined,
        siteExecutorId: undefined,
        userId: undefined,
      });
      setExpandedDayExecutorKeys(new Set());
      setExpandedLedgerIds(new Set());
      setIsFullscreenOpen(true);
    },
  }), []);

  const toggleLedgerRow = useCallback((id: number) => {
    setExpandedLedgerIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const dayExecutorKey = (dateKey: string, siteExecutorId: number) => `${dateKey}::${siteExecutorId}`;

  const toggleDayExecutorGroup = useCallback((dateKey: string, siteExecutorId: number) => {
    const key = dayExecutorKey(dateKey, siteExecutorId);
    setExpandedDayExecutorKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const loadData = useCallback(async () => {
    if (personalOnly && !user?.id) {
      return;
    }

    try {
      setLoading(true);
      setLoadError(null);

      const query: Parameters<typeof ledgerV2Api.getSiteLedger>[1] = {};
      if (filters.startDate) {
        const s = new Date(filters.startDate);
        s.setHours(0, 0, 0, 0);
        query.startDate = s;
      }
      if (filters.endDate) {
        const e = new Date(filters.endDate);
        e.setHours(23, 59, 59, 999);
        query.endDate = e;
      }
      if (filters.siteExecutorId && filters.siteExecutorId > 0) {
        query.siteExecutorId = filters.siteExecutorId;
      } else if (personalOnly && user?.id) {
        query.userId = user.id;
      } else if (filters.userId && filters.userId > 0) {
        query.userId = filters.userId;
      }
      if (!filters.startDate && !filters.endDate) {
        query.limit = 100;
      }

      const balanceQuery: Parameters<typeof ledgerV2Api.getSiteExecutorBalances>[1] = {};
      if (filters.siteExecutorId && filters.siteExecutorId > 0) {
        balanceQuery.siteExecutorId = filters.siteExecutorId;
      } else if (personalOnly && user?.id) {
        balanceQuery.userId = user.id;
      } else if (filters.userId && filters.userId > 0) {
        balanceQuery.userId = filters.userId;
      }

      const [data, balancesData] = await Promise.all([
        ledgerV2Api.getSiteLedger(siteId, query),
        ledgerV2Api.getSiteExecutorBalances(siteId, balanceQuery),
      ]);

      const seen = new Set<number>();
      const deduped: LedgerV2Item[] = [];
      for (const row of data) {
        const id = row.id;
        if (seen.has(id)) continue;
        seen.add(id);
        deduped.push(row);
      }
      setItems(deduped);
      setExecutorBalances(balancesData);
      setHasLedgerAccess(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setHasLedgerAccess(false);
        setItems([]);
        setExecutorBalances([]);
        setLoadError(null);
      } else {
        console.error('Ledger load error', err);
        setLoadError('Не удалось загрузить журнал');
        setItems([]);
        setExecutorBalances([]);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, filters.startDate, filters.endDate, filters.siteExecutorId, filters.userId, personalOnly, user?.id]);

  useEffect(() => {
    if (!isCollapsed || isFullscreenOpen) {
      loadData();
    }
  }, [isCollapsed, isFullscreenOpen, loadData]);

  const displayItems = items;

  const statsData: StatItem[] = useMemo(() => {
    const fmtRub = (n: number) =>
      `${n.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;

    const totalBalance = executorBalances.reduce((s, x) => s + (Number(x.balance) || 0), 0);
    const periodActive = !!(filters.startDate || filters.endDate);

    const sumIn = displayItems.reduce((s, x) => {
      const a = x.amount ?? 0;
      return a > 0 ? s + a : s;
    }, 0);
    const sumOut = displayItems.reduce((s, x) => {
      const a = x.amount ?? 0;
      return a < 0 ? s + a : s;
    }, 0);
    const net = displayItems.reduce((s, x) => s + (x.amount ?? 0), 0);

    const balanceColor: StatItem['color'] =
      totalBalance > 0 ? 'success' : totalBalance < 0 ? 'error' : 'muted';

    const first: StatItem = {
      id: 'current-balance',
      value: fmtRub(totalBalance),
      label: 'Текущий баланс',
      icon: 'ri-wallet-3-line',
      color: balanceColor,
    };

    if (periodActive) {
      return [
        first,
        {
          id: 'net-period',
          value: fmtRub(net),
          label: 'Сальдо за период',
          icon: 'ri-exchange-line',
          color: net >= 0 ? 'primary' : 'warning',
        },
        {
          id: 'credits',
          value: fmtRub(sumIn),
          label: 'Поступления за период',
          icon: 'ri-arrow-down-circle-line',
          color: 'success',
        },
        {
          id: 'debits',
          value: fmtRub(Math.abs(sumOut)),
          label: 'Списания за период',
          icon: 'ri-arrow-up-circle-line',
          color: 'error',
        },
      ];
    }

    return [
      first,
      {
        id: 'rows',
        value: displayItems.length,
        label: 'Проводок в списке (до 100 без периода)',
        icon: 'ri-list-check-2',
        color: 'primary',
      },
      {
        id: 'executors',
        value: executorBalances.length,
        label: 'Исполнителей в балансе',
        icon: 'ri-team-line',
        color: 'info',
      },
    ];
  }, [displayItems, executorBalances, filters.startDate, filters.endDate]);

  /** Ключ календарного дня в локальной таймзоне — для группировки списка */
  const localDayKey = (iso: string) => {
    const d = parseApiDateTimeUtc(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const ledgerByDate = useMemo(() => {
    const dayMap = new Map<string, LedgerV2Item[]>();
    for (const row of displayItems) {
      const key = localDayKey(row.createdAt);
      if (!dayMap.has(key)) dayMap.set(key, []);
      dayMap.get(key)!.push(row);
    }
    const sortedDayKeys = Array.from(dayMap.keys()).sort((a, b) => b.localeCompare(a));
    return sortedDayKeys.map((dateKey) => {
      const dayRows = dayMap.get(dateKey)!;
      const label = parseApiDateTimeUtc(dayRows[0].createdAt).toLocaleDateString('ru-RU', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

      const byExecutor = new Map<number, LedgerV2Item[]>();
      for (const row of dayRows) {
        const eid = row.siteExecutorId;
        if (!byExecutor.has(eid)) byExecutor.set(eid, []);
        byExecutor.get(eid)!.push(row);
      }

      const executorGroups = Array.from(byExecutor.entries())
        .map(([siteExecutorId, rows]) => {
          const accrualsAndCorrectionsSum = roundMoneyClient(sumAccrualsAndCorrections(rows));
          const paymentsSum = roundMoneyClient(sumPayments(rows));
          const chron = sortLedgerChronologically(rows);
          const netDay = roundMoneyClient(chron.reduce((s, r) => s + (r.amount ?? 0), 0));
          const lastRow = chron[chron.length - 1];
          const balanceAfterDay = lastRow?.balanceAfter ?? 0;

          const executorDisplayName = rows[0]?.executorDisplayName || 'Исполнитель';

          return {
            siteExecutorId,
            executorDisplayName,
            rows,
            accrualsAndCorrectionsSum,
            paymentsSum,
            netDay,
            balanceAfterDay,
          };
        })
        .sort((a, b) => {
          const n = a.executorDisplayName.localeCompare(b.executorDisplayName, 'ru');
          return n !== 0 ? n : a.siteExecutorId - b.siteExecutorId;
        });

      return { dateKey, label, executorGroups };
    });
  }, [displayItems]);

  const formatTimeOnly = (iso: string) => {
    const d = parseApiDateTimeUtc(iso);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const moneyLocaleOpts: Intl.NumberFormatOptions = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const formatAmount = (a: number) => {
    const sign = a > 0 ? '+' : '';
    return `${sign}${a.toLocaleString('ru-RU', moneyLocaleOpts)} ₽`;
  };

  const formatBalanceAfter = (a: number) => `${a.toLocaleString('ru-RU', moneyLocaleOpts)} ₽`;

  return (
    <DashboardCard
      id={personalOnly ? 'my-finances-ledger-card' : 'executor-balances-card'}
      title={title}
      icon="ri-wallet-3-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="fullscreen"
      isFullscreenOpen={isFullscreenOpen}
      onFullscreenOpenChange={setIsFullscreenOpen}
      hideCollapsedCard={listHidden}
      headerActions={
        <button
          type="button"
          className="card-action-btn"
          onClick={(e) => {
            e.stopPropagation();
            loadData();
          }}
          disabled={loading || !hasLedgerAccess}
          title="Обновить"
        >
          <i className={loading ? 'ri-loader-4-line ri-spin' : 'ri-refresh-line'} />
        </button>
      }
    >
      {!hasLedgerAccess ? (
        <div className={styles.permissionsInfo}>
          <i className="ri-shield-keyhole-line" />
          <span>
            {personalOnly
              ? 'Личные финансы доступны только участникам объекта. Если вы в объекте, но видите это сообщение — обратитесь к администратору.'
              : 'Нет права «Просмотр финансовых данных». Журнал транзакций доступен при том же доступе, что и карточка «Заработок» и «Платежи сотрудникам».'}
          </span>
        </div>
      ) : (
        <>
          <StatsGrid items={statsData} />

          <div className={styles.filterWrap}>
            <BaseFilter
              filters={baseFilters}
              onFiltersChange={handleBaseFiltersChange}
              filterConfig={filterConfig}
              siteId={siteId}
              includeExternalExecutors={true}
              showLoading={loading}
            />
          </div>

          {loadError && (
            <div className={styles.errorState}>
              <i className="ri-error-warning-line" />
              <span>{loadError}</span>
              <button type="button" className={styles.retryButton} onClick={() => loadData()}>
                <i className="ri-refresh-line" />
                Повторить
              </button>
            </div>
          )}

          {!loadError && !loading && displayItems.length === 0 && (
            <div className={styles.emptyState}>
              <i className="ri-wallet-3-line" />
              <div className={styles.emptyStateTitle}>Проводок нет</div>
              <div className={styles.emptyStateSub}>Измените период или исполнителя либо дождитесь движений по объекту</div>
            </div>
          )}

          {loading && displayItems.length === 0 && !loadError && (
            <div className={styles.emptyState}>
              <i className="ri-loader-4-line ri-spin" />
              <div className={styles.emptyStateTitle}>Загрузка…</div>
            </div>
          )}

          {!loadError && displayItems.length > 0 && (
            <div className={styles.list}>
              {ledgerByDate.map((group) => (
                <div key={group.dateKey} className={styles.dateGroup}>
                  <div className={styles.dateGroupLabel}>{group.label}</div>
                  {group.executorGroups.map((exec) => {
                    const dk = dayExecutorKey(group.dateKey, exec.siteExecutorId);
                    const detailsExpanded = expandedDayExecutorKeys.has(dk);
                    const accentSign =
                      exec.netDay > 0 ? 'plus' : exec.netDay < 0 ? 'minus' : 'zero';

                    return (
                      <React.Fragment key={dk}>
                        <div
                          className={styles.dayExecutorSummary}
                          role="button"
                          tabIndex={0}
                          aria-expanded={detailsExpanded}
                          onClick={() => toggleDayExecutorGroup(group.dateKey, exec.siteExecutorId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleDayExecutorGroup(group.dateKey, exec.siteExecutorId);
                            }
                          }}
                        >
                          <div className={styles.rowAccent} data-sign={accentSign} aria-hidden />
                          <div className={styles.dayExecutorSummaryBody}>
                            <div className={styles.summaryHeader}>
                              {!personalOnly ? (
                                <span className={styles.executor}>{exec.executorDisplayName}</span>
                              ) : (
                                <span className={styles.executor}>Операции за день</span>
                              )}
                              <i
                                className={detailsExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
                                aria-hidden
                              />
                            </div>
                            <ul className={styles.summaryTotals}>
                              <li>
                                <span>Начисления и коррекции</span>
                                <span
                                  className={
                                    exec.accrualsAndCorrectionsSum >= 0
                                      ? styles.amountPlusSummary
                                      : styles.amountMinusSummary
                                  }
                                >
                                  {formatAmount(exec.accrualsAndCorrectionsSum)}
                                </span>
                              </li>
                              <li>
                                <span>Платежи (выплаты)</span>
                                <span
                                  className={
                                    exec.paymentsSum >= 0
                                      ? styles.amountPlusSummary
                                      : styles.amountMinusSummary
                                  }
                                >
                                  {formatAmount(exec.paymentsSum)}
                                </span>
                              </li>
                              <li>
                                <span>Итого за день (начисления + платежи)</span>
                                <span
                                  className={
                                    exec.netDay >= 0 ? styles.amountPlusSummary : styles.amountMinusSummary
                                  }
                                >
                                  {formatAmount(exec.netDay)}
                                </span>
                              </li>
                              <li className={styles.summaryBalanceFinal}>
                                <span>Баланс после всех операций дня</span>
                                <span
                                  className={styles.balanceAfterStrong}
                                  title="Накопительный баланс исполнителя по журналу после последней проводки за этот календарный день."
                                >
                                  {formatBalanceAfter(exec.balanceAfterDay)}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                        {detailsExpanded ? (
                          <div className={styles.detailsBlock}>
                            {exec.rows.map((row) => {
                              const amt = row.amount ?? 0;
                              const sign = amt >= 0 ? 'plus' : 'minus';
                              const ledgerExpandedRow = expandedLedgerIds.has(row.id);
                              const showBalanceAfterRow = row.showsBalanceAfter !== false;
                              const amountClass =
                                amt >= 0 ? styles.amountPlus : amt < 0 ? styles.amountMinus : styles.amountZero;
                              return (
                                <div
                                  key={`ledger-row-${row.id}`}
                                  className={styles.rowNested}
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={ledgerExpandedRow}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLedgerRow(row.id);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleLedgerRow(row.id);
                                    }
                                  }}
                                >
                                  <div className={styles.rowAccent} data-sign={sign} aria-hidden />
                                  <div className={styles.rowBody}>
                                    {!ledgerExpandedRow ? (
                                      <div className={styles.rowMain}>
                                        <div className={styles.rowCompact}>
                                          <span className={styles.rowTime}>{formatTimeOnly(row.createdAt)}</span>
                                          <span className={styles.detailNote}>
                                            {movementDetailLine(row)}
                                          </span>
                                          <span className={`${styles.amount} ${amountClass}`}>
                                            {formatAmount(amt)}
                                          </span>
                                          {showBalanceAfterRow ? (
                                            <span
                                              className={styles.rowBalanceAfterCompact}
                                              title="Баланс после операции"
                                            >
                                              {formatBalanceAfter(row.balanceAfter ?? 0)}
                                            </span>
                                          ) : null}
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div className={styles.rowTop}>
                                          <span className={styles.executor}>
                                            {row.executorDisplayName || 'Исполнитель'}
                                          </span>
                                          <div className={styles.rowTopRight}>
                                            <span className={`${styles.amount} ${amountClass}`}>
                                              {formatAmount(amt)}
                                            </span>
                                          </div>
                                        </div>
                                        <div className={styles.rowMeta}>
                                          {formatTimeOnly(row.createdAt)}
                                          {' · '}
                                          {movementDetailLine(row)}
                                          {showBalanceAfterRow ? (
                                            <>
                                              {' · '}
                                              баланс после: {formatBalanceAfter(row.balanceAfter ?? 0)}
                                            </>
                                          ) : null}
                                        </div>
                                        {row.description ? (
                                          <div className={styles.desc}>{row.description}</div>
                                        ) : null}
                                        {row.createdByName ? (
                                          <div className={styles.by}>Записал: {row.createdByName}</div>
                                        ) : null}
                                      </>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </DashboardCard>
  );
});

ExecutorBalancesCard.displayName = 'ExecutorBalancesCard';

export default ExecutorBalancesCard;
